import React, { useRef, useCallback } from 'react';
import cn from 'classnames';
import Popover, { PopoverRef } from '@dealroadshow/uikit/core/components/Popover';
import Button, { ButtonVariantType } from '@dealroadshow/uikit/core/components/Button';
import Spinner from '@dealroadshow/uikit/core/components/Loader/Spinner';
import { usePortfoliosContext } from '@/finsight/application/PortfolioMonitor/PortfoliosContext';
import {
  IFieldData, TSetFieldsData,
} from '@/finsight/ui/components/portfolioMonitor/PortfolioContent/ManagePortfoliosModal/ManagePortfoliosModal';
import { useManagePortfoliosContext } from '@/finsight/application/PortfolioMonitor/ManagePortfoliosContext';

import styles from './deletePortfolioModal.scss';
import namePortfolioFormStyles from '@/finsight/ui/components/portfolioMonitor/PortfolioContent/PortfolioForm/FormSteps/NamePortfolioForm/namePortfolioForm.scss';

const additionalOffset = {
  height: -10,
  width: 15,
};

interface IProps {
  fieldData: IFieldData,
  visibleDeleteModalId: number,
  deleteModalId: number,
  onCancel: () => void,
  setFieldsData: TSetFieldsData,
  children: React.ReactElement,
}

const DeletePortfolioModal = ({
  fieldData,
  visibleDeleteModalId,
  deleteModalId,
  onCancel,
  setFieldsData,
  children,
}: IProps) => {
  const { setPortfolioCollection } = usePortfoliosContext();
  const { deletePortfolio, isDeletePortfolioFetching, getPortfolios } = useManagePortfoliosContext();
  const deleteButtonStyles = cn(styles.deleteBtn, {
    [styles.deleteBtnFetching]: isDeletePortfolioFetching,
  });
  const popoverRef = useRef<PopoverRef>(null);

  const removeFieldData = () => {
    setFieldsData((prevFieldsData: IFieldData[]) => prevFieldsData
      .filter((currFieldsData) => currFieldsData.id !== fieldData.id));
  };

  const removeNonExistingPortfolio = () => {
    removeFieldData();
  };

  const removeExistingPortfolio = () => {
    setPortfolioCollection((prevPortfolios) => prevPortfolios
      .filter((currPortfolio) => currPortfolio.id !== fieldData.portfolio.id));
    removeFieldData();
  };

  const onDelete = useCallback(async () => {
    const isPortfolioExist = !!fieldData.portfolio.id;
    if (isPortfolioExist) {
      const result = await deletePortfolio(fieldData.portfolio);
      if (result) {
        removeExistingPortfolio();
      }
    }

    removeNonExistingPortfolio();
    await getPortfolios();
    onCancel();
    }, [fieldData]);

  const popperModifiers = [
    {
      name: 'offset',
      options: {
        offset: ({ popper }) => {
          const halfPopperWidth = popper.width / 2;
          return [-halfPopperWidth + additionalOffset.width, additionalOffset.height];
        },
      },
    },
  ];

  const preventClosingPortfolio = (e) => {
    e.stopPropagation();
  };

  const content = (
    <div className={ styles.wrapper } onClick={ preventClosingPortfolio }>
      <div className={ styles.content }>
        <div className={ styles.title }>Delete Portfolio</div>
        <div className={ styles.description }>
          Are you sure you want to delete this portfolio? This action is irreversible.
        </div>
        <div className={ styles.buttons }>
          <Button
            className={ deleteButtonStyles }
            onClick={ onDelete }
            variant={ ButtonVariantType.action }
            dataTest="deleteButton"
            disabled={ isDeletePortfolioFetching }
          >
            { isDeletePortfolioFetching ? <Spinner className={ namePortfolioFormStyles.spinner } /> : 'Delete Portfolio' }
          </Button>
          <Button
            onClick={ onCancel }
            variant={ ButtonVariantType.text }
            title="Cancel"
            dataTest="cancelButton"
          />
        </div>
      </div>
    </div>
  );

  return (
    <Popover
      ref={ popoverRef }
      contentClassName={ styles.popoverContent }
      arrowClassName={ styles.popoverArrow }
      renderOnlyVisible
      isVisible={ visibleDeleteModalId === deleteModalId }
      dataTest="deletePortfolioModal"
      content={ content }
      modifiers={ popperModifiers }
    >
      { children }
    </Popover>
  );
};

export default DeletePortfolioModal;
